import { useLayoutEffect, useRef } from "react";
import { ChatMessage } from "app/modules/aiAssistant/AIAssistantSlice";

const NEAR_BOTTOM_THRESHOLD_PX = 100;

// Keeps a chat's message list scrolled to the latest message, unless the user has scrolled up to read older ones
export default function useFollowLatestMessage(messages: ChatMessage[], isAwaitingReply: boolean, runSteps: string[]) {
  const messageListRef = useRef<HTMLDivElement>(null);
  const shouldFollowLatestRef = useRef(true);
  const lastScrollTopRef = useRef(0);
  // The first scroll is instant, so opening a chat doesn't animate through its whole history
  const nextScrollBehaviourRef = useRef<ScrollBehavior>("auto");

  useLayoutEffect(() => {
    const messageList = messageListRef.current;
    if (!messageList) {
      return;
    }

    // The user's own message always brings them back to the bottom
    if (messages[messages.length - 1]?.isUserAuthor) {
      shouldFollowLatestRef.current = true;
    }

    if (shouldFollowLatestRef.current) {
      messageList.scrollTo({ top: messageList.scrollHeight, behavior: nextScrollBehaviourRef.current });
      nextScrollBehaviourRef.current = "smooth";
    }
  }, [messages, isAwaitingReply, runSteps]);

  const handleScroll = () => {
    const messageList = messageListRef.current;
    if (!messageList) {
      return;
    }

    const distanceFromBottom = messageList.scrollHeight - messageList.scrollTop - messageList.clientHeight;
    if (distanceFromBottom < NEAR_BOTTOM_THRESHOLD_PX) {
      shouldFollowLatestRef.current = true;
    } else if (messageList.scrollTop < lastScrollTopRef.current) {
      // Only scrolling up stops following, so the smooth scroll down doesn't cancel itself
      shouldFollowLatestRef.current = false;
    }
    lastScrollTopRef.current = messageList.scrollTop;
  };

  return { messageListRef, handleScroll };
}
